import * as Sentry from '@sentry/react';
import type React from 'react';

// Sentry Configuration
// The DSN is configurable via the VITE_SENTRY_DSN build-time environment
// variable (see .env.production / .env.staging). The constant below is a
// fallback used when the variable is not provided.
export const SENTRY_DSN_FALLBACK =
  'https://22202d15aa11fcf27cdc1f74b32a728c@o4509960650620928.ingest.de.sentry.io/4509960736669776';

export const getSentryDsn = (): string => {
  const envDsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  return envDsn && envDsn.length > 0 ? envDsn : SENTRY_DSN_FALLBACK;
};

// App build version, injected at build time via Vite `define` (see vite.config.ts).
// Falls back to 'unknown' when not provided (e.g. raw tsc consumers).
export const APP_VERSION: string = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';

// Guards against double-initialisation when initializeSentry() is invoked more
// than once (StrictMode effects, hot reloads, repeated imports).
let sentryInitialized = false;

// Initialize Sentry
export const initializeSentry = () => {
  if (sentryInitialized) {
    return;
  }

  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
  const isProduction = environment === 'production';
  const dsn = getSentryDsn();

  Sentry.init({
    dsn,
    environment,
    // Tag every event with the app build version so issues can be attributed
    // to a specific release in Sentry.
    release: APP_VERSION,
    // Send default PII data to Sentry (as per your configuration)
    sendDefaultPii: true,
    // Sample rate for error events (100% for error tracking)
    sampleRate: 1.0,
    // Sample rate for performance monitoring
    tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in production, 100% in development
    // Enable performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session replay for error debugging
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Session replay sample rates
    replaysSessionSampleRate: isProduction ? 0.01 : 0.1, // 1% in production, 10% in development
    replaysOnErrorSampleRate: 1.0, // 100% when errors occur
    // Performance monitoring
    beforeSend(event, hint) {
      // Filter out non-critical errors in production
      if (isProduction && event.exception) {
        const error = hint.originalException;
        // Skip common non-critical errors
        if (error instanceof Error) {
          if (error.message.includes('ResizeObserver loop limit exceeded') ||
              error.message.includes('Non-Error promise rejection captured') ||
              error.message.includes('ChunkLoadError')) {
            return null;
          }
        }
      }
      return event;
    },
    beforeSendTransaction(event) {
      // Filter out transactions we don't need to monitor
      if (event.transaction === 'GET /favicon.ico') {
        return null;
      }
      return event;
    },
  });

  sentryInitialized = true;
};

// Test-only helper: throws a deliberately tagged error so it can be captured
// and verified in the Sentry project. Rendered behind a dev-only trigger in the
// UI (see components/dev/DevErrorTrigger.tsx); never exposed in production.
export const SENTRY_TEST_ERROR_MESSAGE = '[reverseTax dev trigger] Sentry capture test error';

export const triggerSentryTestError = (): void => {
  const error = new Error(SENTRY_TEST_ERROR_MESSAGE);
  reportError(error, { source: 'dev_error_trigger', appVersion: APP_VERSION });
  throw error;
};

// Error reporting functions
export const reportError = (error: Error, context?: Record<string, unknown>) => {
  Sentry.withScope((scope) => {
    if (context) {
      // Add context to error report
      Object.keys(context).forEach(key => {
        scope.setContext(key, context[key] as Record<string, unknown>);
      });
    }
    Sentry.captureException(error);
  });
  
  console.error('Error reported to Sentry:', error, context);
};

export const reportMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info', context?: Record<string, unknown>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach(key => {
        scope.setContext(key, context[key] as Record<string, unknown>);
      });
    }
    scope.setLevel(level);
    Sentry.captureMessage(message);
  });
  
  console.log(`Message reported to Sentry (${level}):`, message, context);
};

// Performance monitoring - note: startTransaction is deprecated in newer versions
export const startTransaction = (name: string, operation: string) => {
  // For newer Sentry versions, use startSpan instead
  if (typeof Sentry.startSpan === 'function') {
    return Sentry.startSpan({ name, op: operation }, (span) => span);
  }
  
  // Fallback for older versions
  if (typeof (Sentry as unknown as { startTransaction?: unknown }).startTransaction === 'function') {
    return (Sentry as unknown as { startTransaction: (config: { name: string; op: string }) => { setStatus: (status: string) => void; finish: () => void } }).startTransaction({
      name,
      op: operation,
    });
  }
  
  // Return a mock transaction if neither is available
  return {
    setStatus: () => {},
    finish: () => {},
  };
};

// Specific error types for the tax calculator
export const reportCalculationError = (error: Error, calculationInput: Record<string, unknown>) => {
  reportError(error, {
    calculationType: 'tax_calculation',
    input: {
      // Anonymize sensitive data
      hasNetIncome: !!calculationInput.netIncome,
      incomeFrequency: calculationInput.incomeFrequency,
      ageCategory: calculationInput.ageCategory,
      hasAdvancedOptions: !!(calculationInput.medicalAid || calculationInput.pension || calculationInput.travelAllowance),
    },
  });
};

export const reportExportError = (error: Error, exportType: string, exportData: Record<string, unknown> | string | number | boolean | null | undefined) => {
  reportError(error, {
    exportType,
    dataSize: JSON.stringify(exportData).length,
    timestamp: new Date().toISOString(),
  });
};

export const reportPerformanceIssue = (metricName: string, value: number, threshold: number) => {
  if (value > threshold) {
    reportMessage(`Performance issue detected: ${metricName}`, 'warning', {
      metric: metricName,
      value,
      threshold,
      userAgent: navigator.userAgent,
    });
  }
};

// User feedback collection
export const showUserFeedbackDialog = () => {
  Sentry.showReportDialog({
    eventId: Sentry.lastEventId(),
    title: 'Something went wrong',
    subtitle: 'Our team has been notified, but please let us know what happened.',
    subtitle2: 'Your feedback helps us improve the tax calculator.',
    labelName: 'Name (optional)',
    labelEmail: 'Email (optional)',
    labelComments: 'What happened?',
    labelSubmit: 'Submit Feedback',
    successMessage: 'Thank you for your feedback!',
  });
};

// Set user context (without PII)
export const setUserContext = (context: {
  id?: string;
  segment?: string;
  properties?: Record<string, unknown>;
}) => {
  Sentry.setUser({
    id: context.id || 'anonymous',
    segment: context.segment,
    ...context.properties,
  });
};

// Add breadcrumbs for debugging
export const addBreadcrumb = (message: string, category: string, level: 'info' | 'warning' | 'error' = 'info', data?: Record<string, unknown>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

// Tax calculator specific breadcrumbs
export const addCalculationBreadcrumb = (action: string, data?: Record<string, unknown>) => {
  addBreadcrumb(action, 'calculator', 'info', {
    ...data,
    // Ensure no sensitive data is logged
    timestamp: new Date().toISOString(),
  });
};

export const addExportBreadcrumb = (exportType: string, success: boolean) => {
  addBreadcrumb(
    `Export ${exportType} ${success ? 'succeeded' : 'failed'}`,
    'export',
    success ? 'info' : 'error',
    { exportType, success }
  );
};

export const addNavigationBreadcrumb = (from: string, to: string) => {
  addBreadcrumb(
    `Navigation from ${from} to ${to}`,
    'navigation',
    'info',
    { from, to }
  );
};

// Performance monitoring helpers
export const measurePerformance = async <T>(
  name: string,
  operation: () => Promise<T> | T
): Promise<T> => {
  const transaction = startTransaction(name, 'function');
  
  try {
    const result = await operation();
    if ('setStatus' in transaction && typeof transaction.setStatus === 'function') {
      (transaction.setStatus as (status: unknown) => void)('ok');
    }
    return result;
  } catch (error) {
    if ('setStatus' in transaction && typeof transaction.setStatus === 'function') {
      (transaction.setStatus as (status: unknown) => void)('internal_error');
    }
    throw error;
  } finally {
    if ('finish' in transaction && typeof transaction.finish === 'function') {
      transaction.finish();
    }
  }
};

// React Error Boundary integration
export const SentryErrorBoundary = Sentry.withErrorBoundary;

// Higher-order component for error boundaries
export const withSentryErrorBoundary = <P extends Record<string, unknown>>(
  Component: React.ComponentType<P>,
  errorBoundaryOptions?: {
    showDialog?: boolean;
  }
) => {
  return Sentry.withErrorBoundary(Component, {
    beforeCapture: (scope, _error, errorInfo) => {
      // Add additional context
      scope.setContext('errorBoundary', {
        componentStack: errorInfo ? String(errorInfo) : 'unknown',
        errorBoundary: true,
      });
      
      // Show user feedback dialog if enabled
      if (errorBoundaryOptions?.showDialog) {
        setTimeout(() => showUserFeedbackDialog(), 100);
      }
    },
  });
};